import type { CommandPaletteGroup } from '@nuxt/ui'
import type { NuxterSummary } from '#shared/types'
import { createSharedComposable } from '@vueuse/core'

function _useHeaderLinks() {
  const route = useRoute()
  const { meta: docsMeta } = useDocsVersion()

  const headerLinks = computed(() => {
    const to = docsMeta.value.path
    const archived = docsMeta.value.status === 'archived'

    return [{
      label: 'Docs',
      icon: 'i-lucide-book-marked',
      to,
      search: false,
      active: route.path.startsWith(to) || route.path.startsWith(`/deploy`),
      children: [{
        label: 'Get Started',
        description: 'Learn how to get started with Nuxt to build your first app.',
        icon: 'i-lucide-rocket',
        to: `${to}/getting-started/installation`,
        active: route.path.startsWith(`${to}/getting-started`)
      }, {
        label: 'Structure',
        description: 'Learn about the directory structure of a Nuxt project.',
        icon: 'i-lucide-folder-open',
        to: `${to}/directory-structure`,
        active: route.path.startsWith(`${to}/directory-structure`)
      }, {
        label: 'Guide',
        description: 'Get the key concepts, directory structure and best practices.',
        icon: 'i-lucide-book-open',
        to: `${to}/guide`,
        active: route.path.startsWith(`${to}/guide`) && !route.path.startsWith(`${to}/guide/directory-structure`)
      }, {
        label: 'API',
        description: 'Explore the Nuxt components, composables, utilities and more.',
        icon: 'i-lucide-code-xml',
        to: `${to}/api`,
        active: route.path.startsWith(`${to}/api`)
      },
      // Archived versions have no deploy guides, examples or community section.
      ...(archived
        ? []
        : [{
            label: 'Deploy',
            description: 'Deploy your Nuxt project anywhere.',
            icon: 'i-lucide-cloud',
            to: '/deploy',
            active: route.path.startsWith('/deploy')
          }, {
            label: 'Examples',
            description: 'Discover and explore official and community examples.',
            icon: 'i-lucide-app-window-mac',
            to: '/docs/examples',
            active: route.path.startsWith('/docs/examples')
          }, {
            label: 'Community',
            description: 'Find answers and support from the community.',
            icon: 'i-lucide-messages-square',
            to: `${to}/community`,
            active: route.path.startsWith(`${to}/community`)
          }])]
    }, {
      label: 'Modules',
      icon: 'i-lucide-puzzle',
      to: '/modules',
      description: 'Supercharge your Nuxt project with modules.'
    }, {
      label: 'Showcase',
      icon: 'i-lucide-presentation',
      description: 'Discover and explore projects built with Nuxt.',
      to: '/showcase'
    }, {
      label: 'Nuxters',
      icon: 'i-lucide-award',
      description: 'Discover the contributors of the Nuxt ecosystem and share your Nuxter profile.',
      to: '/nuxters',
      active: route.path.startsWith('/nuxters')
    }, {
      label: 'Resources',
      icon: 'i-lucide-library',
      to: '/templates',
      search: false,
      active: ['/templates', '/video-courses', '/enterprise', '/sponsors'].some(path => route.path.startsWith(path)),
      children: [{
        label: 'Templates',
        description: 'Start your next project with a Nuxt template.',
        icon: 'i-lucide-app-window',
        to: '/templates'
      }, {
        label: 'Video Courses',
        description: 'Learn Nuxt by watching video courses.',
        icon: 'i-lucide-graduation-cap',
        to: '/video-courses'
      }, {
        label: 'Agencies',
        description: 'Agencies specialized in Nuxt development.',
        icon: 'i-lucide-handshake',
        to: '/enterprise/agencies'
      }, {
        label: 'Sponsors',
        description: 'Help us sustain Nuxt development.',
        icon: 'i-lucide-hand-heart',
        to: '/sponsors'
      }, {
        label: 'Nuxt Certification',
        description: 'Obtain your Certification of Competence.',
        icon: 'i-lucide-medal',
        to: 'https://certification.nuxt.com',
        target: '_blank'
      }]
    }, {
      label: 'Updates',
      icon: 'i-lucide-newspaper',
      to: '/updates',
      description: 'Releases and blog posts from the Nuxt team.',
      active: route.path.startsWith('/updates') || route.path.startsWith('/blog') || route.path.startsWith('/changelog')
    }]
  })

  return { headerLinks }
}

export const useHeaderLinks = import.meta.client ? createSharedComposable(_useHeaderLinks) : _useHeaderLinks

const footerLinks = [{
  label: 'Community',
  children: [{
    label: 'Nuxters',
    to: '/nuxters'
  }, {
    label: 'Team',
    to: '/team'
  }, {
    label: 'Design Kit',
    to: '/design-kit'
  }]
}, {
  label: 'Explore',
  children: [{
    label: 'Templates',
    to: '/templates'
  }, {
    label: 'Showcase',
    to: '/showcase'
  }, {
    label: 'AI Evals',
    to: '/evals'
  }]
}, {
  label: 'Enterprise',
  children: [{
    label: 'Agencies',
    to: '/enterprise/agencies'
  }, {
    label: 'Sponsors',
    to: '/sponsors'
  }]
}]

export const useFooterLinks = () => ({ footerLinks })

const _useNavigation = () => {
  const searchTerm = ref<string>('')
  const { track } = useAnalytics()
  const { open: openAgent } = useNuxtAgent()

  const { headerLinks } = useHeaderLinks()
  const { footerLinks } = useFooterLinks()
  const { modules, fetchList: fetchModules } = useModules()
  const { providers, fetchList: fetchHosting } = useHostingProviders()
  const { articles: blogArticles, fetchList: fetchBlog } = useBlog()

  const searchLinks = computed(() => [{
    label: 'Ask Nuxi',
    icon: 'i-custom-nuxi',
    to: 'javascript:void(0);',
    onSelect: () => {
      track('Nuxi Opened', { source: 'search-links' })
      openAgent()
    }
  }, ...headerLinks.value.flatMap((link) => {
    if (link.search === false) {
      return (link.children || []).map(child => ({
        ...child,
        label: `${link.label} > ${child.label}`
      }))
    }
    return [link]
  }), {
    label: 'Changelog',
    icon: 'i-lucide-history',
    to: '/changelog'
  }, {
    label: 'Team',
    icon: 'i-lucide-users',
    to: '/team'
  }, {
    label: 'Design Kit',
    icon: 'i-lucide-palette',
    to: '/design-kit'
  }, {
    label: 'AI Evals',
    icon: 'i-lucide-brain',
    to: '/evals'
  }, {
    label: 'Newsletter',
    icon: 'i-lucide-mail',
    to: '/newsletter'
  }, {
    label: 'Source Code',
    icon: 'i-lucide-code',
    to: 'https://github.com/nuxt/nuxt.com',
    target: '_blank'
  }])

  const modulesItems = computed(() => modules.value.map(module => ({
    id: `module-${module.name}`,
    label: module.npm,
    suffix: module.description,
    downloads: module.stats?.downloads ?? 0,
    avatar: {
      src: moduleImage(module.icon),
      ui: {
        root: 'rounded-none bg-transparent'
      }
    },
    to: `/modules/${module.name}`
  })))

  const hostingItems = computed(() => providers.value.map(hosting => ({
    id: `hosting-${hosting.path}`,
    label: hosting.title,
    suffix: hosting.description,
    icon: hosting.logoIcon,
    avatar: hosting.logoSrc
      ? {
          src: hosting.logoSrc,
          ui: {
            root: 'rounded-none bg-transparent'
          }
        }
      : undefined,
    to: hosting.path
  })))

  const blogItems = computed(() => blogArticles.value.map(article => ({
    id: `blog-${article.path}`,
    label: article.title,
    suffix: article.description,
    icon: 'i-lucide-newspaper',
    to: article.path
  })))

  // Nuxters: 27k usernames, so the server searches them (lazy, one request per pause in typing).
  const nuxterResults = ref<NuxterSummary[]>([])
  let nuxtersRequest = 0
  watchDebounced(searchTerm, async (term) => {
    const query = term.trim()
    const current = ++nuxtersRequest
    if (query.length < 2) {
      nuxterResults.value = []
      return
    }
    const items = await $fetch<NuxterSummary[]>('/api/search/nuxters', { query: { q: query, limit: 5 } }).catch(() => [])
    // A slower, older request must not replace newer results.
    if (current === nuxtersRequest) nuxterResults.value = items
  }, { debounce: 200 })

  const { format } = new Intl.NumberFormat('en-US')
  const nuxterItems = computed(() => nuxterResults.value.map(nuxter => ({
    id: `nuxter-${nuxter.githubId}`,
    label: nuxter.username,
    suffix: `Nuxter #${format(nuxter.rank)} · ${format(nuxter.score)} pts`,
    // UAvatar renders with <NuxtImg> (IPX): pass the source path, not an IPX URL.
    avatar: {
      src: `/gh_avatar/${nuxter.username}`,
      alt: nuxter.username,
      densities: 'x1 x2',
      ui: { root: 'rounded-md' }
    },
    to: `/nuxters/${nuxter.username}`
  })))

  const postFilter = (searchTerm: string, items: any[]) => {
    if (!searchTerm) {
      return []
    }
    return items
  }

  const searchGroups = computed<CommandPaletteGroup[]>(() => [{
    id: 'modules-search',
    label: 'Modules',
    items: modulesItems.value,
    postFilter
  }, {
    id: 'hosting-search',
    label: 'Hosting',
    items: hostingItems.value,
    postFilter
  }, {
    id: 'blog-search',
    label: 'Blog',
    items: blogItems.value,
    postFilter
  }, {
    id: 'nuxters-search',
    label: 'Nuxters',
    items: nuxterItems.value,
    // Already filtered and ranked by the server
    ignoreFilter: true,
    postFilter
  }, {
    id: 'ask-ai-search',
    label: 'AI',
    ignoreFilter: true,
    postFilter,
    items: [{
      label: 'Ask Nuxi',
      icon: 'i-custom-nuxi',
      onSelect() {
        track('Nuxi Opened', { source: 'search-palette', query: searchTerm.value })
        openAgent(searchTerm.value)
      }
    }]
  }])

  const { open: searchOpen } = useContentSearch()
  watch(searchOpen, (value) => {
    if (value) {
      fetchModules()
      fetchHosting()
      fetchBlog()
    }
  })

  return {
    searchTerm,
    headerLinks,
    footerLinks,
    searchLinks,
    searchGroups
  }
}

export const useNavigation = import.meta.client ? createSharedComposable(_useNavigation) : _useNavigation
